import { useAuth, useUser } from '@clerk/react-router';
import {
  ArrowRightStartOnRectangleIcon,
  BuildingOffice2Icon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useMachine } from '@xstate/react';
import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { UserContext } from '~/app';
import { Button } from '~/components/buttons/Button/Button';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { ALERT_VIEW_STATE, AppActorSystem } from '~/features/xstateSystem';
import { buildAuthCompletePath, buildSignInPathWithReturnTo } from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import { PROVIDER_AUTH_HOME } from '~/utils/constants';
import { SETTINGS_TABS } from './settings/tabs';
import { verifyAssociationMachine } from './verifyAssociation.machine';

const TAGS = CT.verifyAssociation;

const InvitationLoading = () => (
  <div data-cy={TAGS.loading} className="flex min-h-screen items-center justify-center bg-base">
    <LoadingSpinner size={20} />
  </div>
);

// Shared card for a signed-in account that cannot respond to this invitation: say
// why, and offer the one way out, signing out and back in with the right account.
const SignOutCard = ({
  title,
  children,
  onSignOut,
  cyTag,
  buttonCyTag,
}: {
  title: string;
  children: ReactNode;
  onSignOut: () => void;
  cyTag?: string;
  buttonCyTag?: string;
}) => (
  <div data-cy={cyTag} className="flex min-h-screen items-center justify-center bg-base p-10">
    <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
      <h1 className="mb-3 text-2xl font-semibold text-content-primary">{title}</h1>
      <p className="mb-7 text-sm leading-relaxed text-content-secondary">{children}</p>
      <Button cyTag={buttonCyTag} type="button" onClick={onSignOut}>
        Sign out and continue
      </Button>
    </div>
  </div>
);

const LINK_CLASS =
  'inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold';
const PRIMARY_LINK_CLASS = `${LINK_CLASS} bg-primary-500 text-white hover:bg-primary-600`;
const SECONDARY_LINK_CLASS = `${LINK_CLASS} bg-grey-900 text-grey-200 hover:bg-grey-800`;

// [MCV-1828] An in-app link. A full-page `<a href="/">` lands on the public site,
// which looks signed out even though the session is intact; /home is routed by
// role to the signed-in user's own home.
const HomeLink = ({ children }: { children: ReactNode }) => (
  <Link to={paths.appHome} data-cy={TAGS.continueLink} className={PRIMARY_LINK_CLASS}>
    {children}
  </Link>
);

export const ProviderAssociationVerify = () => {
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const location = useLocation();
  const isProvider = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const needsRegistration = UserContext.useSelector(
    (state) =>
      state.matches('$_CHOOSE_ROLE') ||
      state.matches('$_PROVIDER_REGISTRATION') ||
      state.matches('$_EMPLOYER_REGISTRATION')
  );
  const hasOtherRole = UserContext.useSelector(
    (state) =>
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' }) ||
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_ADMIN' }) ||
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  if (!isLoaded) return <InvitationLoading />;

  if (!isSignedIn) {
    return (
      <Navigate
        to={buildSignInPathWithReturnTo(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }

  // Clerk can settle before the account fetch. Do not offer Accept until the
  // app has resolved the provider, or consume an invite before registration.
  if (needsRegistration) return <Navigate to={buildAuthCompletePath(returnTo)} replace />;

  if (hasOtherRole) {
    return (
      <SignOutCard
        title="Sign in to your provider account"
        onSignOut={() => void signOut({ redirectUrl: returnTo })}
      >
        This invitation must be accepted from the provider account that was invited.
      </SignOutCard>
    );
  }

  if (!isProvider) return <InvitationLoading />;

  return <ProviderAssociationResponse key={location.search} returnTo={returnTo} />;
};

const ProviderAssociationResponse = ({ returnTo }: { returnTo: string }) => {
  const { signOut } = useAuth();
  const { user } = useUser();
  const signedInEmail = user?.primaryEmailAddress?.emailAddress;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code') ?? '';

  const [state, send] = useMachine(verifyAssociationMachine, {
    input: { code },
  });

  const { verifyData, respondError, recipientEmailHint, awaitsSignup, alreadyAssociated } =
    state.context;
  const submitting = state.matches('$_ACCEPTING') || state.matches('$_DECLINING');
  // Both leave the card up with the reason shown and Accept/Decline disabled.
  const resendRequired = state.matches('$_RESEND_REQUIRED') || state.matches('$_EXPIRED');

  const userRef = UserContext.useActorRef();

  useEffect(() => {
    if (state.matches('$_ACCEPTED')) {
      const alertView = (userRef.system as unknown as AppActorSystem).get(ALERT_VIEW_STATE);
      alertView.send({
        type: 'ON_SHOW_ALERT',
        payload: {
          text: `You are now associated with ${verifyData?.employerName}`,
          type: 'success',
          timeout: 5000,
        },
      });
      navigate(PROVIDER_AUTH_HOME);
    }
  }, [state.value, navigate, userRef, verifyData?.employerName]);

  useEffect(() => {
    if (respondError) {
      const alertView = (userRef.system as unknown as AppActorSystem).get(ALERT_VIEW_STATE);
      alertView.send({
        type: 'ON_SHOW_ALERT',
        payload: { text: respondError, type: 'error', timeout: 5000 },
      });
    }
  }, [respondError, userRef]);

  if (state.matches('$_VERIFYING') || state.matches('$_CHECKING_RECIPIENT')) {
    return <InvitationLoading />;
  }

  // [MCV-1804] Signed in as a different provider than the one invited. Say so and
  // offer the way out, rather than an Accept button the server will refuse.
  if (state.matches('$_WRONG_ACCOUNT')) {
    return (
      <SignOutCard
        title="This invitation is for a different account"
        cyTag={TAGS.wrongAccount}
        buttonCyTag={TAGS.wrongAccountSignOutBtn}
        onSignOut={() => void signOut({ redirectUrl: returnTo })}
      >
        {awaitsSignup
          ? `This invitation was sent to ${
              recipientEmailHint ?? 'an email address'
            }, which doesn't have a MedCredVault account yet.`
          : `${verifyData?.employerName ?? 'The employer'} sent this invitation to ${
              recipientEmailHint ?? 'another provider account'
            }.`}
        {signedInEmail ? ` You're signed in as ${signedInEmail}.` : ''}
        {awaitsSignup
          ? ' Sign out, then create an account with that email to accept it.'
          : ' Sign out, then sign in with the invited account to respond.'}
      </SignOutCard>
    );
  }

  if (state.matches('$_INVALID')) {
    return (
      <div
        data-cy={TAGS.invalidMsg}
        className="flex min-h-screen items-center justify-center bg-base p-10"
      >
        <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-danger-subtle">
            <ExclamationTriangleIcon
              data-cy="invite-invalid-icon"
              aria-hidden="true"
              className="h-7 w-7 text-danger"
            />
          </div>
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">Invitation Not Found</h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">
            This invitation link is invalid, has expired, or has already been used. Please contact
            the employer who sent the invitation to request a new one.
          </p>
          <HomeLink>Go to Homepage</HomeLink>
        </div>
      </div>
    );
  }

  if (state.matches('$_DECLINED')) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base p-10">
        <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">Invitation Declined</h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">
            You have declined the invitation from{' '}
            <strong className="text-content-primary">{verifyData?.employerName}</strong>. No
            association has been created.
          </p>
          <HomeLink>Continue</HomeLink>
        </div>
      </div>
    );
  }

  // [MCV-1828] Reached from the invitation notification, which stays in the bell
  // after a decline.
  if (state.matches('$_ALREADY_DECLINED')) {
    return (
      <div
        data-cy={TAGS.alreadyDeclined}
        className="flex min-h-screen items-center justify-center bg-base p-10"
      >
        <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">
            Invitation Already Declined
          </h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">
            You already declined the invitation from{' '}
            <strong className="text-content-primary">{verifyData?.employerName}</strong>.
          </p>
          <HomeLink>Continue</HomeLink>
        </div>
      </div>
    );
  }

  // [MCV-1827] The single-employer rule refused the accept. Say which employer
  // holds the slot and, when it is a different one, where to free it.
  if (state.matches('$_ALREADY_ASSOCIATED')) {
    const currentName = alreadyAssociated?.employerName || 'an employer';
    // A name is not an identity (two employers can share one), so a response
    // without `sameEmployer` is treated as a different employer and keeps the
    // Settings advice.
    const sameEmployer = alreadyAssociated?.sameEmployer === true;
    return (
      <div
        data-cy={TAGS.alreadyAssociated}
        className="flex min-h-screen items-center justify-center bg-base p-10"
      >
        <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">Already Associated</h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">
            You're already associated with{' '}
            <strong className="text-content-primary">{currentName}</strong>.
            {sameEmployer
              ? ''
              : ` A provider can be associated with only one employer at a time. To accept this invitation, disassociate from ${currentName} in Settings first.`}
          </p>
          <div className="flex justify-center gap-3">
            {!sameEmployer && (
              <Link
                to={`${paths.vault.settings}?tab=${SETTINGS_TABS.employerAssociation}`}
                data-cy={TAGS.alreadyAssociatedSettingsLink}
                className={SECONDARY_LINK_CLASS}
              >
                Go to Settings
              </Link>
            )}
            {/* "Continue" read as continuing to disassociate; this only leaves the card. */}
            <HomeLink>Close</HomeLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-cy={TAGS.page} className="flex min-h-screen items-center justify-center bg-base p-10">
      <div className="w-full max-w-lg rounded-lg bg-white p-12 text-left shadow-xl">
        {/* Employer badge */}
        <div
          className="mb-7 flex w-fit mx-auto items-center gap-3 rounded-lg border border-grey-900 bg-grey-950 px-5 py-3"
          data-cy="invite-organization-badge"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-primary-50">
            <BuildingOffice2Icon
              data-cy="invite-organization-icon"
              aria-hidden="true"
              className="h-6 w-6 text-white"
            />
          </div>
          <div>
            <div data-cy={TAGS.employerName} className="font-semibold text-content-primary">
              {verifyData?.employerName}
            </div>
            <div className="text-xs text-content-secondary">Healthcare Organization</div>
          </div>
        </div>

        <h1 className="mb-3 text-2xl font-semibold text-content-primary">
          You've been invited to associate
        </h1>

        <p className="mb-8 text-sm leading-relaxed text-content-secondary">
          <strong className="text-content-primary">{verifyData?.employerName}</strong> has invited
          you to associate your MedCredVault account with their organization. This will allow them
          to view and manage your credentialing vault on your behalf.
        </p>

        <div className="mb-8 space-y-2.5 rounded-lg border border-grey-900 bg-grey-950 p-5">
          <div className="flex items-center gap-2.5">
            <CheckBadgeIcon className="h-[18px] w-[18px] shrink-0 text-primary-50" />
            <span className="text-sm text-content-secondary">
              They can view your profile, credentials, and documents
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <PencilSquareIcon className="h-[18px] w-[18px] shrink-0 text-primary-50" />
            <span className="text-sm text-content-secondary">
              They can edit and update your credentialing information
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <ArrowRightStartOnRectangleIcon className="h-[18px] w-[18px] shrink-0 text-primary-50" />
            <span className="text-sm text-content-secondary">
              You can disassociate at any time from your Settings page
            </span>
          </div>
        </div>

        {resendRequired && (
          <p
            role="alert"
            className="mb-5 rounded-lg bg-danger-subtle p-4 text-sm leading-relaxed text-danger"
          >
            {respondError}
          </p>
        )}

        <div className="flex justify-center gap-3 text-center">
          <Button
            cyTag={TAGS.declineBtn}
            type="button"
            colorVariant="tertiary"
            onClick={() => send({ type: 'ON_DECLINE' })}
            disabled={submitting || resendRequired}
          >
            Decline
          </Button>
          <Button
            cyTag={TAGS.acceptBtn}
            type="button"
            colorVariant="primary"
            onClick={() => {
              send({ type: 'ON_ACCEPT' });
            }}
            disabled={submitting || resendRequired}
          >
            {submitting ? 'Processing...' : 'Accept & Associate'}
          </Button>
        </div>
      </div>
    </div>
  );
};
